import { formatPercent } from "@angular/common";
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import {
  FormFieldCheckbox,
  FormFieldDateAge,
  FormFieldInput,
  FormFieldInputEmail,
  FormFieldInputMobileNumber,
  FormFieldInputNumber,
  FormFieldInputPercentage,
  FormFieldInputTypeahead,
  FormFieldRadio,
  FormFieldRange,
  FormFieldSelect,
  FormFieldToggle,
  HelpDialog,
  HtmlInputMode,
  HtmlInputType,
  InputAutoCompleteAttribute,
  RadioButtonOption,
  SelectOption,
} from "pax-ui/components";
import { FormFieldInputCode } from "projects/pax-ui/components/form-field/model/input/code";
import { ValidationErrorKey } from "projects/pax-ui/components/form-field/validators/general-validation.";
import { InsuranceTermInYearsOption } from "src/app/quote/shared/services/insurance-term-in-years-service";
import { nameValidator } from "src/app/shared/services/form/validators/name.validator";
import {
  AppropriatenessAssessmentQuestion,
  BeneficiaryType,
  EmploymentType,
  Gender,
  PremiumPaymentFrequency,
  SavingsPlan,
  SavingsPlanContainer,
  SwissResidencePermit,
  UnderwritingQuestion,
} from "../../store/quote/shared/shared.state";
import { TestId } from "../../testing/test-id";
import { SWISS_NATIONALITY_CODE } from "../backend/nationality/nationality-http.service";
import { OccupationDto } from "../backend/occupation/occupation-http.service";
import { OccupationFacade } from "../domain/occupation/occupation.facade";

@Injectable({
  providedIn: "root",
})
export class FormComponentService {
  getEmailAddressField() {
    return new FormFieldInputEmail({
      label: $localize`E-Mail`,
      key: FormFieldName.EMAIL,
      required: true,
      testId: TestId.INPUT_FIELD_EMAIL,
      placeholder: "emma.muster@pax.ch",
    });
  }

  getMobileNumberField() {
    return new FormFieldInputMobileNumber({
      label: $localize`Schweizer Mobilnummer`,
      testId: TestId.INPUT_FIELD_MOBILE_PHONE_NUMBER,
      key: FormFieldName.MOBILE_PHONE_NUMBER,
      required: true,
      helpDialog: {
        title: $localize`Schweizer Mobilnummer`,
        contentAsMarkdown: $localize`Für den Abschluss eines Vertrags mit Pax ist eine Schweizer Mobilnummer erforderlich. Dadurch kann der Vertrag bequem und sicher via SMS abgeschlossen werden.`,
      },
      placeholder: "+41334626147", // https://fakenumber.org/other/switzerland
    });
  }

  getAcceptedTermsField(
    termsDataProcessingPdfUrl: URL,
    termsEmailCommunicationPdfUrl: URL,
    termsElectronicDirectSalesUrl: URL,
  ) {
    return new FormFieldCheckbox({
      key: FormFieldName.ACCEPTED_TERMS,
      label: $localize`Bedingungen`,
      isLabelHidden: true,
      required: true,
      options: [
        {
          id: "yes",
          label: $localize`Ich bestätige, die Informationen zur [Datenbearbeitung](${termsDataProcessingPdfUrl.toString()}:TermsDataProcessingDocument link:), für den [elektronischen Direktvertrieb](${termsElectronicDirectSalesUrl.toString()}:TermsElectronicDirectSalesDocument link:) sowie die [Vereinbarung zur Kommunikation via E-Mail](${termsEmailCommunicationPdfUrl.toString()}:TermsEmailCommunicationDocument link:) gelesen und verstanden zu haben.`,
          isMarkdownLabel: true,
          testId: TestId.CHECKBOX_ACCEPT_TERMS_OPTION,
        },
      ],
    });
  }

  getFirstNameField() {
    return new FormFieldInput({
      label: $localize`Vorname`,
      placeholder: "Robin",
      key: FormFieldName.FIRST_NAME,
      required: true,
      isLabelHidden: false,
      testId: TestId.INPUT_FIELD_FIRST_NAME,
      autoComplete: InputAutoCompleteAttribute.GIVEN_NAME,
      validators: [nameValidator],
      errorMessages: {
        [ValidationErrorKey.NAME]: $localize`Ungültiger Vorname`,
      },
    });
  }

  getLastNameField() {
    return new FormFieldInput({
      label: $localize`Nachname`,
      placeholder: "Muster",
      key: FormFieldName.LAST_NAME,
      autoComplete: InputAutoCompleteAttribute.LAST_NAME,
      required: true,
      isLabelHidden: false,
      testId: TestId.INPUT_FIELD_LAST_NAME,
      validators: [nameValidator],
      errorMessages: {
        [ValidationErrorKey.NAME]: $localize`Ungültiger Nachname`,
      },
    });
  }

  getAddressLinesField() {
    return new FormFieldInput({
      label: $localize`Strasse und Hausnummer`,
      placeholder: "Aeschenplatz 13",
      key: FormFieldName.ADDRESS_LINES,
      autoComplete: InputAutoCompleteAttribute.STREET_ADDRESS,
      testId: TestId.INPUT_FIELD_ADDRESS_LINES,
      required: true,
      isLabelHidden: false,
    });
  }

  getPostcodeField(containerClassName?: string, helpDialog?: HelpDialog) {
    const postcodeFormField = new FormFieldInputNumber({
      label: $localize`PLZ`,
      inputType: HtmlInputType.NUMBER,
      inputMode: HtmlInputMode.TEL,
      placeholder: "4001",
      key: FormFieldName.POSTCODE,
      required: true,
      autoComplete: InputAutoCompleteAttribute.POSTAL_CODE,
      testId: TestId.INPUT_FIELD_POSTCODE,
      helpDialog: helpDialog,
      min: 1000,
      max: 9999,
      isLabelHidden: false,
    });

    if (containerClassName) {
      postcodeFormField.containerClassName = containerClassName;
    }

    return postcodeFormField;
  }

  getCityField(containerClassName?: string) {
    const cityFormField = new FormFieldInput({
      label: $localize`Ort`,
      placeholder: "Basel",
      key: FormFieldName.CITY,
      autoComplete: InputAutoCompleteAttribute.CITY_NAME,
      testId: TestId.INPUT_FIELD_CITY,
      required: true,
      isLabelHidden: false,
    });

    if (containerClassName) {
      cityFormField.containerClassName = containerClassName;
    }

    return cityFormField;
  }

  getNationalityField(nationalityOptions?: SelectOption[]) {
    return new FormFieldSelect({
      key: FormFieldName.NATIONALITY,
      label: $localize`Nationalität`,
      testId: TestId.SELECT_FIELD_NATIONALITY,
      required: true,
      helpDialog: {
        title: $localize`Nationalität`,
        contentAsMarkdown: $localize`Bitte geben Sie Ihre Staatsangehörigkeit an. Wählen Sie dazu aus der Liste der verfügbaren Länder Ihre Nationalität aus. Falls Sie mehrere Staatsangehörigkeiten besitzen, wählen Sie bitte eine aus.`,
      },
      options: nationalityOptions ?? [],
      errorMessages: {
        [ValidationErrorKey.REQUIRED]: $localize`Nationalität ist ein Pflichtfeld.`,
      },
    });
  }

  getResidencePermitField() {
    return new FormFieldRadio({
      label: $localize`Aufenthaltsbewilligung`,
      isLabelHidden: false,
      variant: "button",
      key: FormFieldName.RESIDENCE_PERMIT,
      required: true,
      options: RESIDENCE_PERMIT_RADIO_OPTIONS,
      expressions: {
        hide: (values) => {
          return (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- to get rid of 'amy' it would require a major refactoring of the form factory
            !values[FormFieldName.NATIONALITY] ||
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- to get rid of 'amy' it would require a major refactoring of the form factory
            values[FormFieldName.NATIONALITY] === SWISS_NATIONALITY_CODE
          );
        },
      },
    });
  }

  getIsSmokerField() {
    return new FormFieldRadio({
      label: $localize`Rauchen Sie?`,
      variant: "button",
      key: FormFieldName.IS_SMOKER,
      required: true,
      isLabelHidden: true,
      options: YES_OR_NO_RADIO_BUTTON_OPTIONS,
    });
  }

  getGenderField() {
    return new FormFieldRadio({
      label: $localize`Geschlecht`,
      isLabelHidden: true,
      variant: "button",
      key: FormFieldName.GENDER,
      required: true,
      options: [
        { id: "m", value: "M", label: $localize`Mann` },
        { id: "f", value: "F", label: $localize`Frau` },
      ],
    });
  }

  getDateOfBirthRow(
    minimumEntryAge: number | undefined,
    maximumEntryAge: number | undefined,
    className?: string,
  ) {
    return {
      items: [
        this.getDateOfBirthField(minimumEntryAge, maximumEntryAge, false),
      ],
      className: className,
    };
  }

  getDateOfBirthField(
    minimumEntryAge: number | undefined,
    maximumEntryAge: number | undefined,
    isLabelHidden: boolean,
    helpDialog?: HelpDialog,
  ) {
    return new FormFieldDateAge({
      key: FormFieldName.DATE_OF_BIRTH,
      label: $localize`Geburtsdatum`,
      isLabelHidden: isLabelHidden,
      testId: TestId.INPUT_FIELD_DATE_OF_BIRTH,
      required: true,
      helpDialog: helpDialog,
      minAge: minimumEntryAge ?? PLACEHOLDER_MINIMUM_ENTRY_AGE,
      maxAge: maximumEntryAge ?? PLACEHOLDER_MAXIMUM_ENTRY_AGE,
    });
  }

  getInsuranceStartDateField(
    allowedInsuranceStartDateOptions?: SelectOption[],
  ) {
    return new FormFieldSelect({
      key: FormFieldName.INSURANCE_START_DATE,
      label: $localize`Versicherungsbeginn`,
      required: true,
      options: allowedInsuranceStartDateOptions ?? [],
    });
  }

  getOccupationField(occupations?: OccupationDto[], gender?: Gender) {
    return new FormFieldInputTypeahead({
      key: FormFieldName.OCCUPATION_NAME,
      label: $localize`Aktuelle Erwerbstätigkeit`,
      isLabelHidden: false,
      testId: TestId.INPUT_FIELD_TYPEAHEAD_OCCUPATION,
      required: true,
      placeholder: $localize`z. B. Bauzeichner`,
      values: occupations ?? [],
      identifier: OccupationFacade.getOccupationLabelIdentifier(gender),
    });
  }

  getHeightField(minHeightInCm: number, maxHeightInCm: number) {
    return new FormFieldInputNumber({
      label: $localize`Grösse`,
      placeholder: "177",
      key: FormFieldName.HEIGHT_IN_CM,
      required: true,
      isLabelHidden: false,
      inputSuffix: "cm",
      testId: TestId.INPUT_FIELD_HEIGHT,
      helpDialog: {
        title: $localize`Grösse`,
        contentAsMarkdown: $localize`Bitte geben Sie Ihre Körpergrösse in Zentimetern (cm) an.`,
      },
      min: minHeightInCm,
      max: maxHeightInCm,

      errorMessages: {
        min: $localize`Die Körpergrösse muss mindestens ${maxHeightInCm}:Amount in CM: cm betragen.`,
        max: $localize`Die Körpergrösse darf höchstens ${maxHeightInCm}:Amount in CM: cm betragen.`,
      },
    });
  }

  getWeightField(minWeightInKg: number, maxWeightInKg: number) {
    return new FormFieldInputNumber({
      label: $localize`Gewicht`,
      placeholder: "77",
      key: FormFieldName.WEIGHT_IN_KG,
      required: true,
      isLabelHidden: false,
      inputSuffix: "kg",
      testId: TestId.INPUT_FIELD_WEIGHT,
      helpDialog: {
        title: $localize`Gewicht`,
        contentAsMarkdown: $localize`Bitte geben Sie Ihr Körpergewicht in Kilogramm (kg) an.`,
      },
      min: minWeightInKg,
      max: maxWeightInKg,
      errorMessages: {
        [ValidationErrorKey.MIN]: $localize`Das Gewicht muss mindestens ${minWeightInKg}:Amount in Kg: kg betragen.`,
        [ValidationErrorKey.MAX]: $localize`Die Gewicht darf höchstens ${maxWeightInKg}:Amount in Kg: kg betragen.`,
      },
    });
  }

  getIsPregnantField() {
    return new FormFieldCheckbox({
      key: FormFieldName.IS_PREGNANT,
      containerClassName: "flex justify-center",
      isLabelHidden: true,
      required: false,
      options: [
        {
          id: "yes",
          label: $localize`Ich bin schwanger`,
        },
      ],
    });
  }

  getPregnancyMonthField() {
    return new FormFieldInputNumber({
      label: $localize`In welchem Monat sind Sie schwanger?`,
      key: FormFieldName.PREGNANCY_MONTH,
      containerClassName: "flex justify-center",
      isLabelHidden: false,
      required: true,
      min: 1,
      max: 9,
      expressions: {
        hide: (values) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- to get rid of 'amy' it would require a major refactoring of the form factory
          return !values.isPregnant?.yes;
        },
      },
    });
  }

  getBeneficiaryTypeField() {
    return new FormFieldRadio({
      label: $localize`Begünstigung`,
      variant: "button",
      containerClassName: "flex justify-center",
      key: FormFieldName.BENEFICIARY_TYPE,
      required: true,
      isLabelHidden: true,
      options: [
        {
          id: BeneficiaryType.STANDARD,
          value: BeneficiaryType.STANDARD,
          label: $localize`Standard`,
        },
        {
          id: BeneficiaryType.INDIVIDUAL,
          value: BeneficiaryType.INDIVIDUAL,
          label: $localize`Individuell`,
        },
      ],
    });
  }

  public getInsuredSumField() {
    return new FormFieldRange({
      label: $localize`Versicherungssumme`,
      isLabelHidden: true,
      key: FormFieldName.INSURED_SUM,
      required: true,
      testId: TestId.RANGE_FIELD_INSURED_SUM,
      min: 10000,
      max: 2000000,
      step: 10000,
    });
  }

  getInsuranceTermInYearsField(
    durationInYearsOptions: InsuranceTermInYearsOption[] | undefined,
  ) {
    return new FormFieldRadio({
      label: $localize`Für welchen Zeitraum möchten Sie versichert sein?`,
      isLabelHidden: true,
      variant: "button",
      key: FormFieldName.INSURANCE_TERM_IN_YEARS,
      testId: TestId.RADIO_FIELD_INSURANCE_TERM_IN_YEARS,
      required: true,
      options: [
        ...(durationInYearsOptions?.map((insuranceTermInYearsOption) => ({
          id: insuranceTermInYearsOption.toString(),
          value: insuranceTermInYearsOption,
          label: $localize`${insuranceTermInYearsOption}:Amount: Jahre`,
        })) ?? []),
        { id: "other", value: "other", label: $localize`Andere` },
      ],
    });
  }

  getCustomInsuranceTermInYearsField(
    insuranceTermInYearsUpperBound: number | undefined,
  ) {
    const minimumInsuranceTermInYears = 5; // @todo Pass this as part of the ProductConfiguration from backend.
    const maxInsuranceTermInYears =
      insuranceTermInYearsUpperBound ?? PLACEHOLDER_MAX_COVERAGE_AGE;

    return new FormFieldInput({
      key: FormFieldName.CUSTOM_INSURANCE_TERM_IN_YEARS,
      label: $localize`Andere Laufzeit`,
      required: true,
      validators: [
        Validators.min(minimumInsuranceTermInYears),
        Validators.max(maxInsuranceTermInYears),
      ],
      placeholder: $localize`z. B. 20`,
      inputType: HtmlInputType.NUMBER,
      expressions: {
        hide: (values) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return values[FormFieldName.INSURANCE_TERM_IN_YEARS] !== "other";
        },
      },
      errorMessages: {
        [ValidationErrorKey.MIN]: $localize`Die Laufzeit muss mindestens ${minimumInsuranceTermInYears}:Amount of years: Jahre betragen.`,
        [ValidationErrorKey.MAX]: $localize`Die Laufzeit kann maximal ${maxInsuranceTermInYears}:Amount of years: Jahre betragen.`,
      },
    });
  }

  getUnderwritingQuestionField(underwritingQuestion: UnderwritingQuestion) {
    return new FormFieldRadio({
      key: underwritingQuestion.id,
      label: underwritingQuestion.label,
      testId: TestId.RADIO_FIELD_UNDERWRITING_QUESTION,
      variant: "button",
      labelClassName: "pb-2",
      required: true,
      infoText: underwritingQuestion.additionalInfo,
      options: YES_OR_NO_RADIO_BUTTON_OPTIONS,
    });
  }

  getAppropriatenessAssessmentQuestionField(
    appropriatenessAssessmentQuestion: AppropriatenessAssessmentQuestion,
  ) {
    return new FormFieldRadio({
      key: appropriatenessAssessmentQuestion.id,
      label: appropriatenessAssessmentQuestion.label,
      testId: TestId.RADIO_FIELD_UNDERWRITING_QUESTION,
      variant: "radio",
      labelClassName: "pb-2",
      required: true,
      infoText: appropriatenessAssessmentQuestion.additionalInfo,
      options: appropriatenessAssessmentQuestion.answers.map((answer) => ({
        id: answer.id.toString(),
        value: answer.id,
        label: answer.text,
      })),
    });
  }

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Beneficiary
  ///////////////////////////////////////////////////////////////////////////////////////////

  getOrganizationNameField(helpDialog: string) {
    return new FormFieldInput({
      key: FormFieldName.ORGANIZATION_NAME,
      label: $localize`Organisation (Juristische Person)`,
      isLabelHidden: false,
      testId: TestId.INPUT_FIELD_ORGANIZATION_NAME,
      helpDialog: {
        title: $localize`Name der Organisation`,
        contentAsMarkdown: helpDialog,
      },
      required: true,
      placeholder: "Name",
    });
  }

  getPercentageField() {
    return new FormFieldInputPercentage({
      key: FormFieldName.PERCENTAGE,
      containerClassName: "w-32",
      label: $localize`Anteil`,
      placeholder: "50",
      inputType: HtmlInputType.NUMBER,
      inputMode: HtmlInputMode.TEL,
      isLabelHidden: false,
      testId: TestId.INPUT_FIELD_PERCENTAGE,
      helpDialog: {
        title: $localize`Anteil`,
        contentAsMarkdown: $localize`Wählen Sie den prozentualen Anteil des versicherten Todesfallkapitals, den Sie im Falle Ihres Todes der jeweiligen Person oder Organisation zuweisen möchten. Alle Anteile zusammen müssen 100 Prozent ergeben.`,
      },
      required: true,
      min: 1,
      max: 100,
    });
  }

  getEmploymentTypeField() {
    return new FormFieldRadio({
      key: FormFieldName.EMPLOYMENT_TYPE,
      label: $localize`Pensionskasse`,
      required: true,
      variant: "button",
      options: EMPLOYMENT_TYPE_RADIO_OPTIONS,
      isLabelHidden: true,
    });
  }

  getYearlyPremiumField(min: number, max: number) {
    return new FormFieldRange({
      key: FormFieldName.YEARLY_PREMIUM,
      testId: TestId.RANGE_FIELD_YEARLY_PREMIUM,
      required: true,
      min,
      max,
      step: YEARLY_PREMIUM_STEP,
      computedSublabel: {
        calculation: (value) => value / 12,
        suffix: $localize` / Monat`,
      },
      color: "black",
    });
  }

  getAutomaticIncreaseField() {
    return new FormFieldToggle({
      key: FormFieldName.AUTOMATIC_INCREASE,
      label: $localize`Automatische Erhöhung`,
      required: true,
      withBorder: true,
      helpDialog: {
        title: $localize`Automatische Erhöhung`,
        contentAsMarkdown: $localize`Der gesetzliche Maximalbetrag für die Säule 3a steigt regelmässig. Steigt dieser Betrag, wird Ihre Einzahlung automatisch um die Differenz erhöht. So nutzen Sie stets den maximalen Steuervorteil - ohne dass Sie selbst etwas tun müssen.`,
      },
    });
  }

  /* PILLAR 3A */
  getSavingsPlanField(
    savingsPlans: SavingsPlanContainer | undefined,
    _technicalInterestRate: number | undefined,
    locale: string,
  ) {
    const containerClassName = "border p-4 mb-2 w-full";
    const labelClassName = "font-semibold m-0";

    const savingsPlanOptionsSorted = [
      SavingsPlan.SAFE,
      SavingsPlan.BALANCED,
      SavingsPlan.CHANCE,
    ];

    const formFieldOptions = savingsPlanOptionsSorted.map(
      (savingsPlanOption) => {
        const guaranteedPortion =
          savingsPlans?.[savingsPlanOption].guaranteedPortion ?? 0;

        const fundShare = savingsPlans?.[savingsPlanOption].fundShare ?? 0;

        const savingsPlanLabel = SAVINGS_PLAN_LABELS[savingsPlanOption];

        // As for now we can not get the technical interest rate from our backend, since at this point we only have the recommended savings plan,
        // but not the selected one (savingsPlan) which is needed to trigger the calculations in the tariff engine.
        // We end up hardcoding the technical interest rate here.
        // TODO: Get the technical interest rate (0.35 %) dynamically from our backend.

        return {
          id: savingsPlanOption,
          value: savingsPlanOption,
          label: $localize`${savingsPlanLabel}:SavingsPlanLabel:`,
          description: $localize`${formatPercent(guaranteedPortion, locale)}:guaranteedPercent: Garantie, ${formatPercent(fundShare, locale)}:fundPercent: Fondsanlage`,
          labelClassName,
          containerClassName,
          infoDialog: {
            title: $localize`${savingsPlanLabel}:SavingsPlanLabel:`,
            contentAsMarkdown: $localize`Es werden ${formatPercent(
              guaranteedPortion,
              locale,
            )}:guaranteedPercent: Ihrer Spareinlage mit einem festen Zinssatz von 0,35 % verzinst. Dieser Anteil ist damit garantiert.`,
          },
        };
      },
    );

    return new FormFieldRadio({
      variant: "radio",
      key: FormFieldName.SAVINGS_PLAN,
      required: true,
      label: $localize`Je höher der Fondsanteil, desto höher die Renditechancen, aber auch das Verlustrisiko.`,
      labelClassName: "text-base font-semibold",
      options: formFieldOptions,
    });
  }

  getEmailVerificationCodeField() {
    return new FormFieldInputCode({
      label: $localize`E-Mail Code`,
      key: FormFieldName.EMAIL_VERIFICATION_CODE,
      testId: TestId.INPUT_FIELD_EMAIL_VERIFICATION_CODE,
      // Set to false to avoid showing an error message when the form is submitted
      required: false,
    });
  }

  getPremiumPaymentFrequencyField() {
    return new FormFieldRadio({
      variant: "radio",
      key: FormFieldName.PREMIUM_PAYMENT_FREQUENCY,
      testId: TestId.RADIO_FIELD_PREMIUM_PAYMENT_FREQUENCY,
      required: true,
      options: PREMIUM_PAYMENT_FREQUENCY_RADIO_OPTIONS,
    });
  }
}

// This value will be used for the initial rendering when:
// -> The technical age from the backend has not arrived yet
// -> The date of birth has not been set yet and thus we were not able to calculate the technical age in years
//    (e.g the user tried to start the flow from the summary page)
const PLACEHOLDER_MAX_COVERAGE_AGE = 85;

const PLACEHOLDER_MINIMUM_ENTRY_AGE = 18;
const PLACEHOLDER_MAXIMUM_ENTRY_AGE = 100;

export const DEFAULT_INSURED_SUM = 300_000;

export const DEFAULT_SAVINGS_PLAN: SavingsPlan = SavingsPlan.BALANCED;

export const DEFAULT_TECHNICAL_INTEREST_RATE = 0.035;

export enum FormFieldName {
  // Contact Details
  EMAIL = "email",
  EMAIL_VERIFICATION_CODE = "emailVerificationCode",
  MOBILE_PHONE_NUMBER = "mobilePhoneNumber",
  ACCEPTED_TERMS = "acceptedTerms",

  // Personal Details
  FIRST_NAME = "firstName",
  LAST_NAME = "lastName",
  ADDRESS_LINES = "addressLines",
  POSTCODE = "postcode",
  CITY = "city",
  NATIONALITY = "nationality",
  RESIDENCE_PERMIT = "swissResidencePermit",
  INSURANCE_START_DATE = "insuranceStartDate",

  // Smoker
  IS_SMOKER = "isSmoker",

  // Gender
  GENDER = "gender",

  // DateOfBirth
  DATE_OF_BIRTH = "dateOfBirth",

  // Occupation
  OCCUPATION_NAME = "occupationName",

  // EmploymentType
  EMPLOYMENT_TYPE = "employmentType",

  // BMI
  HEIGHT_IN_CM = "heightInCm",
  WEIGHT_IN_KG = "weightInKg",
  IS_PREGNANT = "isPregnant",
  PREGNANCY_MONTH = "pregnancyMonth",

  // Premium
  INSURED_SUM = "insuredSum",
  INSURANCE_TERM_IN_YEARS = "insuranceTermInYears",
  CUSTOM_INSURANCE_TERM_IN_YEARS = "customInsuranceTermInYears",

  // Beneficiary
  BENEFICIARY_TYPE = "beneficiaryType",
  ORGANIZATION_NAME = "organizationName",
  PERCENTAGE = "percentage",

  // Annual Contribution
  YEARLY_PREMIUM = "yearlyPremium",
  AUTOMATIC_INCREASE = "isYearlyPremiumAutoIncrease",

  /* PILLAR 3A */
  SAVINGS_PLAN = "savingsPlan",
  PREMIUM_PAYMENT_FREQUENCY = "premiumPaymentFrequency",
  BROKER = "broker",
}

const RESIDENCE_PERMIT_RADIO_OPTIONS: RadioButtonOption[] = [
  {
    id: SwissResidencePermit.B,
    label: SwissResidencePermit.B,
    value: SwissResidencePermit.B,
  },
  {
    id: SwissResidencePermit.C,
    label: SwissResidencePermit.C,
    value: SwissResidencePermit.C,
  },
];

// TODO: This was modified for a quick fix before the release. Streamline this.
const EMPLOYMENT_TYPE_RADIO_OPTIONS: RadioButtonOption[] = [
  {
    id: EmploymentType.EMPLOYED,
    label: $localize`Ja`,
    value: EmploymentType.EMPLOYED,
  },
  {
    id: EmploymentType.SELF_EMPLOYED,
    label: $localize`Nein`,
    value: EmploymentType.SELF_EMPLOYED,
  },
];

export const PREMIUM_PAYMENT_FREQUENCY_RADIO_OPTIONS: RadioButtonOption[] = [
  {
    id: PremiumPaymentFrequency.YEARLY,
    value: PremiumPaymentFrequency.YEARLY,
    label: $localize`Jährlich`,
  },
  {
    id: PremiumPaymentFrequency.HALF_YEARLY,
    value: PremiumPaymentFrequency.HALF_YEARLY,
    label: $localize`Halbjährlich`,
  },
  {
    id: PremiumPaymentFrequency.QUARTERLY,
    value: PremiumPaymentFrequency.QUARTERLY,
    label: $localize`Vierteljährlich`,
  },
  {
    id: PremiumPaymentFrequency.MONTHLY,
    value: PremiumPaymentFrequency.MONTHLY,
    label: $localize`Monatlich`,
  },
];

enum YesOrNoRadioButtonOptionId {
  YES = "yes",
  NO = "no",
}

export const YES_OR_NO_RADIO_BUTTON_OPTIONS: RadioButtonOption[] = [
  {
    id: YesOrNoRadioButtonOptionId.YES,
    value: true,
    label: $localize`Ja`,
  },
  {
    id: YesOrNoRadioButtonOptionId.NO,
    value: false,
    label: $localize`Nein`,
  },
];

export interface TermsAcceptedFormField {
  yes?: boolean | undefined;
}

export interface IsPregnantFormField {
  yes?: boolean | null;
}

export const SAVINGS_PLAN_LABELS: Record<SavingsPlan, string> = {
  [SavingsPlan.SAFE]: $localize`Sicherheit`,
  [SavingsPlan.BALANCED]: $localize`Ausgewogen`,
  [SavingsPlan.CHANCE]: $localize`Chance`,
};

export const YEARLY_PREMIUM_STEP = 50;
